import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Home, Check, Upload, MessageCircle, Share2 } from 'lucide-react';
import { SEO } from '../components/common/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { AddressAutocomplete } from '../components/common/AddressAutocomplete';
import { TermsAgreement, POST_AD_TERMS } from '../components/smartqr/SmartQRTerms';
import { categories, offerTypes, cities, getAreasForCity } from '../data/constants';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const PostAdPage = () => {
  const [form, setForm] = useState({
    title: '', description: '', price: '', category: '', city: localStorage.getItem('selectedCity') || 'Pune', area: '',
    latitude: '', longitude: '', maps_link: '', phone: '', business_name: '', image_url: '', address: '', offer_type: 'Discount'
  });
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [success, setSuccess] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [customCity, setCustomCity] = useState('');
  const [customArea, setCustomArea] = useState('');

  const isOtherCity = form.city === 'Other';

  const handleCityChange = (city) => {
    setForm({ ...form, city, area: '', latitude: '', longitude: '' });
    if (city !== 'Other') {
      setCustomCity('');
      setCustomArea('');
    }
  };

  const handleAreaChange = (areaName) => {
    const area = getAreasForCity(form.city).find(a => a.name === areaName);
    if (area) setForm({ ...form, area: area.name, latitude: area.lat, longitude: area.lng });
  };

  const handleAddressSelect = (placeData) => {
    // Update form with address data from Google Places
    setForm(prev => ({
      ...prev,
      address: placeData.address,
      latitude: placeData.latitude,
      longitude: placeData.longitude,
      maps_link: placeData.mapsLink,
      // Only update city if detected and matches our supported cities
      city: cities.includes(placeData.city) ? placeData.city : prev.city
    }));
    
    // Try to match area from the address
    if (placeData.area) {
      const matchedArea = getAreasForCity(form.city).find(
        a => a.name.toLowerCase().includes(placeData.area.toLowerCase()) ||
             placeData.area.toLowerCase().includes(a.name.toLowerCase().split(' ')[0])
      );
      if (matchedArea) {
        setForm(prev => ({ ...prev, area: matchedArea.name }));
      }
    }
  };

  const handleImageChange = (e) => { const file = e.target.files[0]; if (file) { setImageFile(file); setImagePreview(URL.createObjectURL(file)); } };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Determine final city and area values
    const finalCity = isOtherCity ? customCity : form.city;
    const finalArea = isOtherCity ? customArea : form.area;
    
    if (!form.business_name || !form.title || !form.description || !form.category || !finalCity || !finalArea || !form.address) { 
      toast.error('Please fill all required fields'); 
      return; 
    }
    if (!termsAccepted) {
      toast.error('Please read and agree to the Terms and Conditions before submitting your ad');
      return; 
    }
    
    // Extra validation for custom city/area
    if (isOtherCity && (!customCity.trim() || !customArea.trim())) {
      toast.error('Please enter your city and area name');
      return;
    }
    
    setLoading(true);
    try {
      let imageUrl = form.image_url;
      if (imageFile) {
        const formData = new FormData();
        formData.append('file', imageFile);
        const uploadRes = await axios.post(`${API}/api/upload/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        imageUrl = uploadRes.data.url;
      }
      
      // Submit with final city/area values
      const submitData = {
        ...form,
        city: finalCity,
        area: finalArea,
        image_url: imageUrl,
        terms_accepted: true,
        price: form.price ? parseFloat(form.price) : null,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null
      };
      
      await axios.post(`${API}/api/deals/submit`, submitData);
      setSuccess(true);
      toast.success('Deal submitted for review!');
    } catch (e) { toast.error('Failed to submit deal'); }
    setLoading(false);
  };

  const handleWhatsAppShare = () => {
    const dealUrl = `${window.location.origin}/deals`;
    const priceText = form.price ? `₹${form.price}` : '';
    const message = `🎉 Check out my new deal on JustOffs!\n\n*${form.title}*${priceText ? ` - ${priceText}` : ''}\n📍 ${form.area}, ${form.city}\n\n${form.description.substring(0, 100)}${form.description.length > 100 ? '...' : ''}\n\n👉 Discover more local deals: ${dealUrl}\n\n#JustOffs #LocalDeals #${form.city.replace(/\s/g, '')}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleCopyLink = () => {
    const dealUrl = `${window.location.origin}/deals`;
    navigator.clipboard.writeText(dealUrl);
    toast.success('Link copied to clipboard!');
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50"><Header />
        <div className="max-w-xl mx-auto px-4 py-16 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5"><Check className="w-8 h-8 text-green-500" /></div>
          <h1 className="text-xl font-bold text-slate-800 mb-3">Deal Submitted!</h1>
          <p className="text-slate-600 text-sm mb-6">Your deal has been submitted for review and will be live soon.</p>
          
          {/* Share Section */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6 mb-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Share Your Deal!</h2>
            <p className="text-slate-600 text-sm mb-4">Spread the word to your customers and WhatsApp groups</p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button 
                onClick={handleWhatsAppShare}
                className="flex items-center justify-center gap-2 bg-green-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-600 transition shadow-md"
                data-testid="share-whatsapp-btn"
              >
                <MessageCircle className="w-5 h-5" /> Share on WhatsApp
              </button>
              <button 
                onClick={handleCopyLink}
                className="flex items-center justify-center gap-2 bg-white border-2 border-slate-200 text-slate-700 px-6 py-3 rounded-xl font-semibold hover:border-slate-300 transition"
                data-testid="copy-link-btn"
              >
                <Share2 className="w-5 h-5" /> Copy Link
              </button>
            </div>
          </div>

          <Link to="/" className="inline-flex items-center gap-2 bg-orange-500 text-white px-5 py-2.5 rounded-lg font-medium text-sm hover:bg-orange-600 transition" data-testid="back-home"><Home className="w-4 h-4" /> Back to Home</Link>
        </div><Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="post-ad-page">
      <SEO title="Post Free Ad - JustOffs | Share Your Deal" description="Post your business deal for free and reach thousands of local customers in Pune. It's 100% free to list your offers." />
      <Header />
      <div className="max-w-lg mx-auto px-4 py-6">
        <h1 className="text-xl font-bold text-slate-800 mb-1">Post Free Ad</h1>
        <p className="text-slate-500 text-sm mb-6">Share your deal with local customers</p>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-5 space-y-4" data-testid="post-ad-form">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Business Name *</label>
            <input type="text" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm" placeholder="Your business name" required data-testid="business-name-input" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Deal Title *</label>
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm" placeholder="e.g., 50% off on all pizzas" required data-testid="title-input" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Offer Type *</label>
            <select value={form.offer_type} onChange={(e) => setForm({ ...form, offer_type: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm" required data-testid="offer-type-select">
              {offerTypes.map(type => (<option key={type.id} value={type.id}>{type.emoji} {type.name}</option>))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Category *</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm" required data-testid="category-select">
              <option value="">Select category</option>
              {categories.map(cat => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">City *</label>
            <select value={form.city} onChange={(e) => handleCityChange(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm" required data-testid="city-select">
              {cities.map(city => (<option key={city} value={city}>{city}</option>))}
            </select>
          </div>

          {/* Custom City Input - shown when "Other" is selected */}
          {isOtherCity && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Enter Your City *</label>
              <input 
                type="text" 
                value={customCity} 
                onChange={(e) => setCustomCity(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm" 
                placeholder="e.g., Chandigarh, Lucknow, Bhopal..." 
                required 
                data-testid="custom-city-input" 
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Area/Locality *</label>
            {isOtherCity ? (
              <input 
                type="text" 
                value={customArea} 
                onChange={(e) => setCustomArea(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm" 
                placeholder="e.g., MG Road, Main Market, Civil Lines..." 
                required 
                data-testid="custom-area-input" 
              />
            ) : (
              <select value={form.area} onChange={(e) => handleAreaChange(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm" required data-testid="area-select">
                <option value="">Select area</option>
                {getAreasForCity(form.city).map(area => (<option key={area.id} value={area.name}>{area.name}</option>))}
              </select>
            )}
            {!isOtherCity && (
              <p className="text-xs text-slate-400 mt-1.5">Don't see your area? Select "Other" city to type manually.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Address *</label>
            <AddressAutocomplete
              value={form.address}
              onChange={(value) => setForm({ ...form, address: value })}
              onSelect={handleAddressSelect}
              placeholder="Start typing your shop/business address"
            />
            {form.latitude && form.longitude && (
              <p className="text-xs text-green-600 mt-1">
                📍 Location detected: {form.latitude.toFixed(4)}, {form.longitude.toFixed(4)}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Description *</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm h-24" placeholder="Describe your deal..." required data-testid="description-input" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Price (₹)</label>
            <input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm" placeholder="Deal price" data-testid="price-input" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Upload Image</label>
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center hover:border-orange-300 transition cursor-pointer" onClick={() => document.getElementById('image-upload').click()} data-testid="image-upload-area">
              {imagePreview ? <img src={imagePreview} alt="Preview" className="max-h-32 mx-auto rounded-lg" /> : (
                <><Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" /><p className="text-sm text-slate-500">Click to upload image</p></>
              )}
              <input id="image-upload" type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Or Image URL</label>
            <input type="url" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm" placeholder="https://example.com/image.jpg" data-testid="image-url-input" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone Number</label>
            <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm" placeholder="Your phone number (optional)" data-testid="phone-input" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Google Maps Link</label>
            <input type="url" value={form.maps_link} onChange={(e) => setForm({ ...form, maps_link: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm" placeholder="https://maps.google.com/..." data-testid="maps-link-input" />
          </div>

          <TermsAgreement terms={POST_AD_TERMS} title="Terms and Conditions for posting an advertisement (tap to read)" checked={termsAccepted} onChange={setTermsAccepted} label="I have read and agree to the Terms and Conditions for posting this advertisement." testPrefix="post-ad-terms" />
          {!termsAccepted && <p className="text-[11px] text-slate-400 text-center" data-testid="post-ad-terms-hint">Agree to the Terms and Conditions to submit your ad.</p>}

          <button type="submit" disabled={loading} className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600 transition disabled:opacity-70" data-testid="submit-btn">
            {loading ? 'Submitting...' : 'Submit Deal'}
          </button>
        </form>
      </div>
      <Footer />
    </div>
  );
};
